import { AnimatePresence, motion } from 'framer-motion';
import { usePrefersReducedMotion } from '../lib/motionShared';

const OM_SYMBOL_URL = 'https://upload.wikimedia.org/wikipedia/commons/6/67/Aum_Om_red.svg';
const TRADITIONAL_GRADIENT = 'linear-gradient(135deg, #c8860a 0%, #e05c00 55%, #b02020 100%)';

const LOADER_PHASES = new Set(['verifying', 'booking', 'receipt', 'inquirySubmitting']);

const ShopOrderFlowOverlay = ({
  phase,
  orderResult,
  inquiryResult,
  onContinue,
  onDownloadPdf,
  onOpenWhatsApp,
  t,
}) => {
  const reduceMotion = usePrefersReducedMotion();
  const isLoader = LOADER_PHASES.has(phase);
  const isSuccess = phase === 'success';
  const isInquirySuccess = phase === 'inquirySuccess';
  const orderNo = orderResult?.order?.orderNo;
  const inquiryNo = inquiryResult?.inquiry?.inquiryNo;

  if (!isLoader && !isSuccess && !isInquirySuccess) return null;

  const phaseMessage = isLoader ? t(`orderFlow.${phase}`) : null;
  const dialogLabel = isInquirySuccess
    ? t('orderFlow.inquirySuccessTitle')
    : isSuccess
      ? t('orderFlow.bookedTitle')
      : phaseMessage;

  return (
    <>
      <style>{`
        @keyframes shopFlowRingA {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes shopFlowRingB {
          from { transform: rotate(0deg); }
          to   { transform: rotate(-360deg); }
        }
        @keyframes shopFlowOmPulse {
          0%, 100% { opacity: 0.88; transform: scale(1); filter: brightness(10) saturate(0) drop-shadow(0 0 12px rgba(255,255,255,0.4)); }
          50%      { opacity: 1; transform: scale(1.08); filter: brightness(10) saturate(0) drop-shadow(0 0 24px rgba(255,255,255,0.7)); }
        }
        @keyframes shopFlowDotBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
          40%           { transform: translateY(-8px); opacity: 1; }
        }
      `}</style>

      <AnimatePresence>
        {(isLoader || isSuccess || isInquirySuccess) && (
          <motion.div
            className="fixed inset-0 z-[190] flex items-center justify-center overflow-hidden p-4"
            style={{ background: TRADITIONAL_GRADIENT }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.35 }}
            role="dialog"
            aria-modal="true"
            aria-live="polite"
            aria-label={dialogLabel}
          >
            {isLoader && (
              <motion.div
                className="flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98 }}
              >
                <div
                  className="relative flex items-center justify-center"
                  style={{ width: 'min(200px, 52vmin)', height: 'min(200px, 52vmin)' }}
                >
                  <div
                    className="absolute rounded-full border-2 border-dashed border-white/30"
                    style={{
                      width: 'min(200px, 52vmin)',
                      height: 'min(200px, 52vmin)',
                      animation: reduceMotion ? 'none' : 'shopFlowRingA 12s linear infinite',
                    }}
                  />
                  <div
                    className="absolute rounded-full border-2 border-dotted border-white/45"
                    style={{
                      width: 'min(148px, 38vmin)',
                      height: 'min(148px, 38vmin)',
                      animation: reduceMotion ? 'none' : 'shopFlowRingB 8s linear infinite',
                    }}
                  />
                  <div
                    className="absolute rounded-full border border-white/60"
                    style={{
                      width: 'min(100px, 26vmin)',
                      height: 'min(100px, 26vmin)',
                      animation: reduceMotion ? 'none' : 'shopFlowRingA 5s linear infinite',
                    }}
                  />
                  <img
                    src={OM_SYMBOL_URL}
                    alt=""
                    aria-hidden="true"
                    style={{
                      width: 'min(64px, 16vmin)',
                      height: 'min(64px, 16vmin)',
                      animation: reduceMotion ? 'none' : 'shopFlowOmPulse 2.4s ease-in-out infinite',
                      filter: 'brightness(10) saturate(0)',
                    }}
                  />
                </div>

                <p className="mt-6 px-4 font-heading text-lg font-semibold tracking-wide text-white drop-shadow sm:text-xl">
                  श्री गणेशाय नमः
                </p>
                <p className="mt-3 max-w-xs px-4 text-sm text-white/90 sm:text-base">
                  {phaseMessage}
                </p>

                <div className="mt-5 flex gap-2">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="block h-2 w-2 rounded-full bg-white/70"
                      style={{
                        animation: reduceMotion ? 'none' : `shopFlowDotBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </motion.div>
            )}

            {isSuccess && (
              <motion.div
                className="w-full max-w-md rounded-2xl border-2 border-amber-200/80 bg-[#fffdf5]/95 p-6 text-center shadow-2xl backdrop-blur-sm sm:p-8"
                initial={{ opacity: 0, scale: 0.92, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: reduceMotion ? 0 : 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-amber-300/60 bg-amber-50/80">
                  <img
                    src={OM_SYMBOL_URL}
                    alt=""
                    aria-hidden="true"
                    className="h-8 w-8 object-contain"
                    style={{ filter: 'sepia(1) saturate(2) hue-rotate(-10deg)' }}
                  />
                </div>

                <div className="mb-4 flex items-center justify-center gap-3">
                  <span className="h-px w-10 bg-amber-400/70" aria-hidden="true" />
                  <span className="text-xs font-medium uppercase tracking-[0.2em] text-amber-800/80">
                    ✦
                  </span>
                  <span className="h-px w-10 bg-amber-400/70" aria-hidden="true" />
                </div>

                <h2 className="font-heading text-xl font-bold leading-snug text-amber-950 sm:text-2xl">
                  {t('orderFlow.bookedTitle')}
                </h2>

                {orderNo && (
                  <p className="mt-3 text-sm leading-relaxed text-amber-900/85">
                    {t('orderFlow.bookedSubtitle', { orderNo })}
                  </p>
                )}

                <div className="mt-6 flex flex-col gap-3">
                  {orderResult?.order?._id && onDownloadPdf && (
                    <button
                      type="button"
                      onClick={onDownloadPdf}
                      className="min-h-11 w-full rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-900 transition hover:bg-amber-100"
                    >
                      {t('orderFlow.downloadPdf')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onContinue}
                    className="min-h-11 w-full rounded-lg bg-amber-800 px-4 py-2.5 text-sm font-semibold text-amber-50 shadow-md transition hover:bg-amber-900"
                  >
                    {t('orderFlow.continueShopping')}
                  </button>
                </div>
              </motion.div>
            )}

            {isInquirySuccess && (
              <motion.div
                className="w-full max-w-md rounded-2xl border-2 border-amber-200/80 bg-[#fffdf5]/95 p-6 text-center shadow-2xl backdrop-blur-sm sm:p-8"
                initial={{ opacity: 0, scale: 0.92, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: reduceMotion ? 0 : 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-amber-300/60 bg-amber-50/80">
                  <img
                    src={OM_SYMBOL_URL}
                    alt=""
                    aria-hidden="true"
                    className="h-8 w-8 object-contain"
                    style={{ filter: 'sepia(1) saturate(2) hue-rotate(-10deg)' }}
                  />
                </div>

                <div className="mb-4 flex items-center justify-center gap-3">
                  <span className="h-px w-10 bg-amber-400/70" aria-hidden="true" />
                  <span className="text-xs font-medium uppercase tracking-[0.2em] text-amber-800/80">
                    ✦
                  </span>
                  <span className="h-px w-10 bg-amber-400/70" aria-hidden="true" />
                </div>

                <h2 className="font-heading text-xl font-bold leading-snug text-amber-950 sm:text-2xl">
                  {t('orderFlow.inquirySuccessTitle')}
                </h2>

                {inquiryNo && (
                  <p className="mt-3 text-sm font-semibold leading-relaxed text-amber-900">
                    {t('orderFlow.inquirySuccessSubtitle', { inquiryNo })}
                  </p>
                )}

                <p className="mt-3 text-sm leading-relaxed text-amber-900/85">
                  {t('orderFlow.inquirySuccessBody')}
                </p>

                <div className="mt-6 flex flex-col gap-3">
                  {onOpenWhatsApp && (
                    <button
                      type="button"
                      onClick={onOpenWhatsApp}
                      className="min-h-11 w-full rounded-lg bg-amber-800 px-4 py-2.5 text-sm font-semibold text-amber-50 shadow-md transition hover:bg-amber-900"
                    >
                      {t('orderFlow.openWhatsApp')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onContinue}
                    className="min-h-11 w-full rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-900 transition hover:bg-amber-100"
                  >
                    {t('orderFlow.continueShopping')}
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ShopOrderFlowOverlay;
